import { z } from "zod";
import { emptyToUndefined } from "@opus-dt/shared-types";

const EnvSchema = z.object({
  MODE: z.enum(["development", "test", "production"]),
  VITE_SENTRY_DSN: z.preprocess(emptyToUndefined, z.string().url().optional()),
});

export type Env = z.infer<typeof EnvSchema>;

let cached: Env | null = null;

function resolveEnvSource(): Record<string, unknown> {
  const source: Record<string, unknown> = { ...import.meta.env };
  const runtime = typeof window !== "undefined" ? window.__OPUS_CONFIG__ : undefined;
  if (!runtime) return source;

  if (runtime.sentryDsn) source.VITE_SENTRY_DSN = runtime.sentryDsn;

  return source;
}

export function loadEnv(): Env {
  if (cached) return cached;
  const parsed = EnvSchema.safeParse(resolveEnvSource());
  if (!parsed.success) {
    const errors = parsed.error.flatten().fieldErrors;
    const summary = Object.entries(errors)
      .map(([key, msgs]) => `  ${key}: ${(msgs ?? []).join(", ")}`)
      .join("\n");
    renderEnvError(summary);
    throw new Error("[env] Invalid environment configuration");
  }
  cached = parsed.data;
  return parsed.data;
}

function renderEnvError(summary: string): void {
  if (typeof document === "undefined") return;
  document.body.innerHTML = `
    <div style="font-family: ui-monospace, monospace; padding: 32px; max-width: 720px; margin: 40px auto; color: #1f2937;">
      <h1 style="font-size: 20px; margin-bottom: 12px;">Configuração de ambiente inválida</h1>
      <p style="margin-bottom: 12px;">A aplicação não pôde iniciar porque uma ou mais variáveis de ambiente estão ausentes ou em formato incorreto.</p>
      <pre style="background: #f3f4f6; padding: 16px; border-radius: 6px; overflow-x: auto; font-size: 13px;">${summary}</pre>
      <p style="margin-top: 16px; font-size: 13px; color: #6b7280;">Consulte <code>apps/web/.env.example</code> para o formato esperado de cada variável. Valores recebidos não são exibidos por segurança.</p>
    </div>
  `;
}
